<template>
  <div class="input-label flex flex-col gap-6">

    <div v-if="loading" class="text-gray-400 text-sm">{{ t('loading') }}</div>

    <template v-else>

      <!-- ===== TERMINALS ===== -->
      <div class="flex flex-col gap-3">
        <div class="flex items-center gap-3">
          <label class="text-base font-medium text-gray-700">{{ t('terminals') }}</label>
          <el-button type="primary" circle size="small" class="!w-7 !h-7" @click="addTerminalRow">
            <el-icon><Plus /></el-icon>
          </el-button>
        </div>

        <div v-if="terminals.length === 0 && !newTerminalRow" class="text-sm text-gray-400">
          {{ t('noTerminals') }}
        </div>

        <!-- existing terminals -->
        <div v-for="terminal in terminals" :key="terminal.id" class="flex items-center gap-2">
          <el-input
            v-model="terminal.enName"
            :placeholder="t('terminalName')"
            class="flex-1"
            @input="terminal.changed = true"
          />
          <el-select
            v-model="terminal.epaymentDestinationAccountID"
            :placeholder="t('selectDestination')"
            class="flex-1"
            filterable
            clearable
            @change="terminal.changed = true"
          >
            <el-option
              v-for="bank in banks"
              :key="bank.id"
              :label="ctx.locale === 'ar' ? bank.arName : bank.enName"
              :value="bank.id"
            />
          </el-select>
          <el-button
            type="primary"
            size="small"
            :class="{ invisible: !terminal.changed }"
            @click="saveTerminal(terminal)"
          >{{ t('save') }}</el-button>
          <el-button type="danger" text class="!p-1" @click="deleteTerminal(terminal)">
            <el-icon size="18"><Close /></el-icon>
          </el-button>
        </div>

        <!-- new terminal row -->
        <div v-if="newTerminalRow" class="flex items-center gap-2">
          <el-input
            v-model="newTerminalRow.enName"
            :placeholder="t('terminalName')"
            class="flex-1"
            ref="newTerminalInput"
          />
          <el-select
            v-model="newTerminalRow.epaymentDestinationAccountID"
            :placeholder="t('selectDestination')"
            class="flex-1"
            filterable
            clearable
          >
            <el-option
              v-for="bank in banks"
              :key="bank.id"
              :label="ctx.locale === 'ar' ? bank.arName : bank.enName"
              :value="bank.id"
            />
          </el-select>
          <el-button type="primary" size="small" @click="createTerminal">{{ t('add') }}</el-button>
          <el-button size="small" @click="newTerminalRow = null">{{ t('cancel') }}</el-button>
        </div>
      </div>

      <!-- DIVIDER -->
      <div class="border-t border-dashed border-gray-200" />

      <!-- ===== REGISTERS ===== -->
      <div class="flex flex-col gap-3">
        <label class="text-base font-medium text-gray-700">{{ t('registers') }}</label>

        <template v-for="(group, locationName) in registersByLocation" :key="locationName">
          <div class="flex flex-col gap-2">
            <div class="text-sm font-semibold text-[#1D4C75]">{{ locationName }}</div>

            <div
              v-for="(register, idx) in group"
              :key="register.id"
              class="flex items-start gap-4"
            >
              <span class="text-sm text-gray-400 shrink-0 pt-1.5">#{{ idx + 1 }}</span>

              <div class="flex-1 min-w-0">
                <el-input
                  v-model="register.customName"
                  class="w-full"
                  @input="register.changed = true"
                />
              </div>

              <!-- terminals column -->
              <div class="flex flex-col gap-1.5 shrink-0">
                <span class="text-xs text-gray-400">{{ t('terminal') }}</span>
                <label
                  v-for="terminal in terminals"
                  :key="terminal.id"
                  class="flex items-center gap-1.5 text-sm cursor-pointer select-none"
                >
                  <el-checkbox
                    :model-value="isTerminalEnabled(register, terminal.id)"
                    @change="(val: boolean) => { toggleTerminal(register, terminal.id, val) }"
                  />
                  {{ terminal.enName }}
                </label>
              </div>

              <el-button
                type="primary"
                size="small"
                class="shrink-0 mt-0.5"
                :class="{ invisible: !register.changed }"
                @click="saveRegister(register)"
              >{{ t('update') }}</el-button>
            </div>
          </div>
        </template>
      </div>

    </template>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { onBeforeMount, ref, computed, nextTick } from 'vue'
import { useHTTP } from '@/plugins/http'
import { useContext } from '@/plugins/context'
import { Register } from '@/models/company/Register'
import { Terminal } from '@/models/company/Terminal'
import { Account } from '@/models/accounting/Account'
import { Plus, Close } from '@element-plus/icons-vue'

const messages = {
  en: {
    loading: 'Loading...',
    terminals: 'Terminals',
    noTerminals: 'No terminals yet. Click + to add one.',
    terminalName: 'Name',
    selectDestination: 'Destination account',
    banks: 'Banks',
    save: 'Save',
    add: 'Add',
    cancel: 'Cancel',
    registers: 'Registers',
    register: 'Register',
    terminal: 'Terminal',
    update: 'Update',
  },
  ar: {
    loading: 'جاري التحميل...',
    terminals: 'الأجهزة',
    noTerminals: 'لا توجد أجهزة بعد. اضغط + لإضافة جهاز.',
    terminalName: 'الاسم',
    selectDestination: 'الحساب الوجهة',
    banks: 'البنوك',
    save: 'حفظ',
    add: 'إضافة',
    cancel: 'إلغاء',
    registers: 'الصناديق',
    register: 'الصندوق',
    terminal: 'الجهاز',
    update: 'تحديث',
  },
}

const { t } = useI18n({ messages, useScope: 'global' })
const http = useHTTP()
const ctx = useContext()

defineProps(['setting'])

interface TerminalRow extends Terminal {
  changed: boolean
}

interface RegisterRow {
  id: string
  customName: string
  originalName: string
  changed: boolean
  enabledTerminalIDs: Set<string>
  locationName: string
}

const terminals = ref<TerminalRow[]>([])
const registers = ref<RegisterRow[]>([])
const banks = ref<Account[]>([])
const loading = ref(false)
const newTerminalRow = ref<Partial<Terminal> | null>(null)
const newTerminalInput = ref<any>(null)

const registersByLocation = computed(() => {
  const groups: Record<string, RegisterRow[]> = {}
  for (const reg of registers.value) {
    const loc = reg.locationName || 'Unknown'
    if (!groups[loc]) groups[loc] = []
    groups[loc].push(reg)
  }
  return groups
})

onBeforeMount(async () => {
  loading.value = true
  try {
    const [terminalsData, registersData, banksData] = await Promise.all([
      http.get<Terminal[]>(Terminal.ENDPOINT),
      http.get<any[]>('/company/registers-with-location'),
      http.get<Account[]>('accounting/accounts/banks?skip_balance=true'),
    ])

    terminals.value = (terminalsData || []).map((t) => ({ ...Terminal.from(t), changed: false }))
    banks.value = (banksData || []).map((b) => Account.from(b))

    registers.value = (registersData || []).map((r: any) => {
      const enabledIDs = new Set<string>(
        (r.registerTerminals || [])
          .filter((rt: any) => rt.enabled)
          .map((rt: any) => rt.terminalID as string)
      )
      return {
        id: r.id,
        customName: ctx.locale === 'ar' ? r.arName : r.enName,
        originalName: ctx.locale === 'ar' ? r.arName : r.enName,
        changed: false,
        enabledTerminalIDs: enabledIDs,
        locationName: r.locationName || '',
      }
    })
  } finally {
    loading.value = false
  }
})

async function addTerminalRow() {
  newTerminalRow.value = { enName: '', arName: '', epaymentDestinationAccountID: '' }
  await nextTick()
  newTerminalInput.value?.focus()
}

async function createTerminal() {
  if (!newTerminalRow.value?.enName) return
  const payload = {
    enName: newTerminalRow.value.enName,
    arName: newTerminalRow.value.enName,
    epaymentDestinationAccountID: newTerminalRow.value.epaymentDestinationAccountID || null,
  }
  const created = await http.post<Terminal>(Terminal.ENDPOINT, payload)
  if (created) {
    terminals.value.push({ ...Terminal.from(created), changed: false })
  }
  newTerminalRow.value = null
}

async function saveTerminal(terminal: TerminalRow) {
  await http.put(`${Terminal.ENDPOINT}/${terminal.id}`, {
    enName: terminal.enName,
    arName: terminal.arName,
    epaymentDestinationAccountID: terminal.epaymentDestinationAccountID || null,
  })
  terminal.changed = false
}

async function deleteTerminal(terminal: TerminalRow) {
  await http.delete(`${Terminal.ENDPOINT}/${terminal.id}`)
  terminals.value = terminals.value.filter((t) => t.id !== terminal.id)
}

function isTerminalEnabled(register: RegisterRow, terminalID: string): boolean {
  return register.enabledTerminalIDs.has(terminalID)
}

function toggleTerminal(register: RegisterRow, terminalID: string, enabled: boolean) {
  if (enabled) {
    register.enabledTerminalIDs.add(terminalID)
  } else {
    register.enabledTerminalIDs.delete(terminalID)
  }
  register.changed = true
}

async function saveRegister(register: RegisterRow) {
  if (register.customName !== register.originalName) {
    await http.put(`${Register.ENDPOINT}/${register.id}/update-name`, {
      id: register.id,
      key: ctx.locale === 'ar' ? 'arName' : 'enName',
      value: register.customName,
    })
    register.originalName = register.customName
    if (ctx.currentRegister?.id === register.id) {
      ctx.setRegister(Object.assign(ctx.currentRegister, { [ctx.locale === 'ar' ? 'arName' : 'enName']: register.customName }))
    }
  }

  const assignments = terminals.value.map((t) => ({
    terminalID: t.id,
    enabled: register.enabledTerminalIDs.has(t.id),
  }))
  await http.put(`${Register.ENDPOINT}/${register.id}/terminals`, assignments)

  register.changed = false
}
</script>
